import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EFFECTIVE_DATE } from '../../config/legal';
import './Legal.css';

const SIBLINGS = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/returns', label: 'Returns & Shipping' },
];

/**
 * Shared shell for the three legal pages.
 *
 * Builds its contents list from the section headings it is given, so the
 * navigation cannot fall out of step with the document the way a hand-written
 * list of anchors does.
 */
export default function LegalPage({ title, summary, sections, children }) {
  const [activeId, setActiveId] = useState(sections?.[0]?.id);

  const ids = useMemo(() => (sections || []).map((section) => section.id), [sections]);

  useEffect(() => {
    if (!ids.length || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    ids.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [ids]);

  return (
    <main className="legal-page">
      <header className="legal-hero">
        <div className="legal-shell">
          <p className="legal-kicker">Guhso legal</p>
          <h1>{title}</h1>
          {summary && <p className="legal-summary">{summary}</p>}
          <p className="legal-effective">
            Effective <time dateTime={EFFECTIVE_DATE}>{EFFECTIVE_DATE}</time>
          </p>
          <nav className="legal-switch" aria-label="Legal documents">
            {SIBLINGS.map((sibling) => (
              <Link
                key={sibling.to}
                to={sibling.to}
                className={sibling.label === title ? 'is-current' : ''}
                aria-current={sibling.label === title ? 'page' : undefined}
              >
                {sibling.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <div className="legal-shell legal-body">
        {sections?.length > 0 && (
          <nav className="legal-toc" aria-label="On this page">
            <p className="legal-toc__title">On this page</p>
            <ol>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className={activeId === section.id ? 'is-active' : ''}>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="legal-content">{children}</article>
      </div>
    </main>
  );
}

/** A numbered section with a stable anchor the contents list can target. */
export function Section({ id, title, index, children }) {
  return (
    <section id={id} className="legal-section" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`}>
        {index !== undefined && <span className="legal-section__index">{index}</span>}
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Pulled-out box for the things people most often get wrong. */
export function KeyPoint({ children, tone = 'neutral' }) {
  return <aside className={`legal-keypoint legal-keypoint--${tone}`}>{children}</aside>;
}
